import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';

import { Teacher, teachersMe } from '@api';

import { TranslocoService } from '@jsverse/transloco';

import { firstValueFrom } from 'rxjs';

import { AppSettingsService } from '../../../app-settings.service';
import { buildLoginAppUrl } from '../auth/login-app-url';
import { LECKE_ADMIN_ROLE_CODE } from '../constants/mtp-role-codes';
import { formatPersonFullName } from '../utils/person-full-name.util';
import { UserApiService } from './api/user.api.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  readonly #appSettingsService = inject(AppSettingsService);
  readonly #userApiService = inject(UserApiService);
  readonly #transloco = inject(TranslocoService);

  readonly userData: WritableSignal<Teacher | null> = signal<Teacher | null>(null);
  readonly isProfileDialogOpen = signal<boolean>(false);
  readonly #profileCheckPending = signal<boolean>(false);

  readonly hasProfile = computed(() => this.userData()?.hasProfile ?? false);
  readonly canAccessUserManagement = computed(
    () => this.userData()?.roleCodes?.includes(LECKE_ADMIN_ROLE_CODE) ?? false,
  );
  // Two-letter monogram (e.g. "Kovács Anna" → "KA"), ordered to match
  // `userFullName`: hu = last+first initial, otherwise first+last. A computed
  // (not a getter) so consumers reactively pick up the async `getMe()` load.
  readonly userInitials = computed(() => {
    const user = this.userData();
    if (!user) return '';
    const firstInitial = (user.firstName ?? '').trim().charAt(0);
    const lastInitial = (user.lastName ?? '').trim().charAt(0);
    const ordered =
      this.#transloco.getActiveLang() === 'hu' ? `${lastInitial}${firstInitial}` : `${firstInitial}${lastInitial}`;
    return ordered.toUpperCase();
  });
  readonly tenantInstitutionName = computed((): string | null => {
    const name = this.userData()?.tenant?.name?.trim();
    return name ? name : null;
  });

  /** Read-only: true while a `getMe()` network call is in flight, false once it resolves (success or error). */
  readonly profileCheckPending: Signal<boolean> = this.#profileCheckPending.asReadonly();
  /** Single-flight in-flight request — parallel `getMe()` callers share this promise. */
  #getMePromise: Promise<Teacher | null> | null = null;
  /** Cached, resolved result — once populated, subsequent `getMe()` calls skip the network entirely. */
  #hasCachedTeacher = false;

  // Plain getter (not a computed signal): re-evaluates per change-detection cycle.
  // Live language switching while a consumer is open is not a v0.3 scenario; converting
  // to a langChanges$-backed computed would change the public shape and the consumer template.
  get userFullName(): string {
    const user = this.userData();
    if (!user) return '';
    return formatPersonFullName(user.firstName ?? '', user.lastName ?? '', this.#transloco.getActiveLang());
  }

  getMe(): Promise<Teacher | null> {
    if (this.#hasCachedTeacher) {
      return Promise.resolve(this.userData());
    }

    if (this.#getMePromise) {
      return this.#getMePromise;
    }

    this.#profileCheckPending.set(true);

    this.#getMePromise = this.#fetchMe().finally(() => {
      this.#profileCheckPending.set(false);
      this.#getMePromise = null;
    });

    return this.#getMePromise;
  }

  /** Optimistically marks the signed-in teacher as having a saved profile (post-create). */
  markProfileSaved(): void {
    this.userData.update((user) => (user ? { ...user, hasProfile: true } : user));
  }

  async logout(): Promise<void> {
    await firstValueFrom(this.#userApiService.logout());
    this.isProfileDialogOpen.set(false);

    const loginAppFeUrl = this.#appSettingsService.appConfig?.LOGIN_APP_FE_URL?.trim();
    if (loginAppFeUrl) {
      globalThis.location.href = buildLoginAppUrl(
        loginAppFeUrl,
        window.location.href,
        this.#appSettingsService.appConfig?.MTP_APPLICATION_CODE,
      );
    }
  }

  async #fetchMe(): Promise<Teacher | null> {
    try {
      const response = await teachersMe();
      if (response.error || !response.data) {
        throw new Error('Failed to load user.');
      }
      this.userData.set(response.data ?? null);
      this.#hasCachedTeacher = true;
      return this.userData();
    } catch (error) {
      console.error('Error getting user data', error);
      throw error;
    }
  }
}
